import { useEffect, useRef } from 'react'

import { ButtonGroup } from '@chakra-ui/react'
import {
  SubtoolbarAddVertexIcon,
  SubtoolbarConnectVertexIcon,
  SubtoolbarMoveVertexIcon,
  SubtoolbarRemoveVertexIcon,
} from '@editor/assets/icons'
import { CursorState } from '@editor/config/constants/cursor'
import { SubtoolVertex } from '@editor/interfaces/tool'
import { useAppDispatch } from '@editor/store/app'
import { useVertexSelector } from '@editor/store/selectors'
import { setDefaultCursor } from '@editor/store/tool'
import { setCurrentVertexSubtool } from '@editor/store/toolUtils/vertex'

import SubToolbarButton from '../SubToolbarButton'

/**
 * Cursor mapping for each vertex subtool.
 */
const Cursors: Partial<Record<SubtoolVertex, CursorState>> = {
  [SubtoolVertex.AddVertex]: CursorState.CROSSHAIR,
  [SubtoolVertex.ConnectVertex]: CursorState.POINTER,
}

const VertexToolbar = () => {
  const dispatch = useAppDispatch()
  const availableVertexSubtools = useVertexSelector((state) => state.availableVertexSubtools)
  const currentVertexSubtool = useVertexSelector((state) => state.currentVertexSubtool)
  const disableVertexSubtools = useVertexSelector((state) => state.disableVertexSubtools)
  const disableSwitchingVertexSubtool = useVertexSelector((state) => state.disableSwitchingVertexSubtool)

  // Refs - track subtool before modifier key was pressed
  const previousSubtoolRef = useRef<SubtoolVertex | null>(null)

  // store in refs to avoid re-renders
  const availableVertexSubtoolsRef = useRef(availableVertexSubtools)
  const currentVertexSubtoolRef = useRef(currentVertexSubtool)
  const disableSwitchingVertexSubtoolRef = useRef(disableSwitchingVertexSubtool)

  useEffect(() => {
    availableVertexSubtoolsRef.current = availableVertexSubtools
    currentVertexSubtoolRef.current = currentVertexSubtool
    disableSwitchingVertexSubtoolRef.current = disableSwitchingVertexSubtool
  }, [availableVertexSubtools, currentVertexSubtool, disableSwitchingVertexSubtool])

  /**
   * Change cursor based on selected subtool, clear if no subtool is available.
   */
  useEffect(() => {
    if (availableVertexSubtools.length) {
      dispatch(setDefaultCursor(Cursors[currentVertexSubtool]))
    } else {
      dispatch(setDefaultCursor(undefined))
    }
  }, [availableVertexSubtools, currentVertexSubtool, dispatch])

  /**
   * Handle Alt/Option key to temporarily toggle between AddVertex and RemoveVertex subtools.
   * Following standard drawing software conventions for smooth editing workflow.
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard events when vertex subtools are available
      if (!availableVertexSubtoolsRef.current.length || disableSwitchingVertexSubtoolRef.current) {
        return
      }

      // Check if Alt key is pressed (Option on Mac)
      if (e.altKey && !previousSubtoolRef.current) {
        // Only handle if we haven't already switched (avoid repeated keydown events)
        // And only for certain subtools (not for MoveVertex or ConnectVertex)
        if (
          currentVertexSubtoolRef.current === SubtoolVertex.AddVertex ||
          currentVertexSubtoolRef.current === SubtoolVertex.RemoveVertex
        ) {
          // Prevent default browser behavior for Alt key
          e.preventDefault()

          // Save the current subtool
          previousSubtoolRef.current = currentVertexSubtoolRef.current

          // Switch to the opposite behavior
          if (currentVertexSubtoolRef.current === SubtoolVertex.RemoveVertex) {
            // If already removing, switch to adding
            dispatch(setCurrentVertexSubtool(SubtoolVertex.AddVertex))
          } else {
            // Otherwise, temporarily switch to remove
            dispatch(setCurrentVertexSubtool(SubtoolVertex.RemoveVertex))
          }
        }
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      // Only handle keyboard events when vertex subtools are available
      if (!availableVertexSubtoolsRef.current.length) {
        return
      }

      // When Alt key is released, restore previous subtool
      // At this point, `e.altKey` is false, so we check the key/code instead
      if ((e.key === 'Alt' || e.code === 'AltLeft' || e.code === 'AltRight') && previousSubtoolRef.current) {
        e.preventDefault()
        dispatch(setCurrentVertexSubtool(previousSubtoolRef.current))
        previousSubtoolRef.current = null
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
      // If component unmounts while Alt is pressed, restore the previous subtool
      if (previousSubtoolRef.current) {
        dispatch(setCurrentVertexSubtool(previousSubtoolRef.current))
        previousSubtoolRef.current = null
      }
    }
  }, [dispatch])

  if (!availableVertexSubtools.length) return null

  return (
    <ButtonGroup attached bgColor="gray2.800" borderRadius="md">
      {availableVertexSubtools.includes(SubtoolVertex.AddVertex) && (
        <SubToolbarButton
          icon={<SubtoolbarAddVertexIcon width="1.2em" height="1.2em" />}
          label="頂点を追加"
          toolType={SubtoolVertex.AddVertex}
          selected={currentVertexSubtool === SubtoolVertex.AddVertex}
          disabled={disableVertexSubtools}
          tooltipPlacement="top"
          size="lg"
          onClick={() => {
            dispatch(setCurrentVertexSubtool(SubtoolVertex.AddVertex))
            previousSubtoolRef.current = null
          }}
        />
      )}
      {availableVertexSubtools.includes(SubtoolVertex.RemoveVertex) && (
        <SubToolbarButton
          icon={<SubtoolbarRemoveVertexIcon width="1.2em" height="1.2em" />}
          label="頂点を削除 / 辺を切断"
          toolType={SubtoolVertex.RemoveVertex}
          selected={currentVertexSubtool === SubtoolVertex.RemoveVertex}
          tooltipPlacement="top"
          size="lg"
          onClick={() => {
            dispatch(setCurrentVertexSubtool(SubtoolVertex.RemoveVertex))
            previousSubtoolRef.current = null
          }}
        />
      )}
      {availableVertexSubtools.includes(SubtoolVertex.MoveVertex) && (
        <SubToolbarButton
          icon={<SubtoolbarMoveVertexIcon width="1.2em" height="1.2em" />}
          label="点を移動"
          toolType={SubtoolVertex.MoveVertex}
          selected={currentVertexSubtool === SubtoolVertex.MoveVertex}
          disabled={disableVertexSubtools}
          tooltipPlacement="top"
          size="lg"
          onClick={() => {
            dispatch(setCurrentVertexSubtool(SubtoolVertex.MoveVertex))
            previousSubtoolRef.current = null
          }}
        />
      )}
      {availableVertexSubtools.includes(SubtoolVertex.ConnectVertex) && (
        <SubToolbarButton
          icon={<SubtoolbarConnectVertexIcon width="1.2em" height="1.2em" />}
          label="頂点を接続 (2つの延長を結合)"
          toolType={SubtoolVertex.ConnectVertex}
          selected={currentVertexSubtool === SubtoolVertex.ConnectVertex}
          tooltipPlacement="top"
          size="lg"
          onClick={() => {
            dispatch(setCurrentVertexSubtool(SubtoolVertex.ConnectVertex))
            previousSubtoolRef.current = null
          }}
        />
      )}
    </ButtonGroup>
  )
}

export default VertexToolbar
